import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getLeaderboard } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { 
  Trophy, 
  Medal, 
  Award, 
  ArrowLeft, 
  Crown,
  Sparkles,
  Calendar,
  Loader2
} from "lucide-react";

export default function LeaderboardPage() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch leaderboard on mount
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getLeaderboard();
        setLeaderboard(data || []);
      } catch (err) {
        console.error('Failed to fetch leaderboard:', err);
        setError('Could not load leaderboard. Please try again.');
        setLeaderboard([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLeaderboard();
  }, []);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Award className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="font-display font-bold text-muted-foreground">#{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return "bg-gradient-to-r from-yellow-100 to-yellow-50 border-yellow-300";
      case 2:
        return "bg-gradient-to-r from-gray-100 to-gray-50 border-gray-300";
      case 3:
        return "bg-gradient-to-r from-amber-100 to-amber-50 border-amber-300";
      default:
        return "bg-card border-border";
    }
  };

  // Prestige emblem configuration
  const getPrestigeEmblem = (prestige, isMaster = false) => {
    // Prestige Master emblem
    if (isMaster || prestige === 6) {
      return {
        numeral: 'M',
        name: 'Prestige Master',
        className: 'bg-gradient-to-r from-slate-300 via-zinc-100 to-slate-300 text-slate-800 shadow-slate-400/60 shadow-lg font-black',
        glowClass: 'ring-2 ring-slate-200/90',
        animation: 'animate-platinum-shine',
        special: true,
        isMaster: true,
      };
    }
    
    const emblems = {
      1: {
        numeral: 'I',
        name: 'Prestige I',
        className: 'bg-gradient-to-r from-purple-600 to-violet-500 text-white shadow-purple-500/50 shadow-sm',
        glowClass: 'ring-1 ring-purple-400/50',
      },
      2: {
        numeral: 'II',
        name: 'Prestige II',
        className: 'bg-gradient-to-r from-emerald-500 to-green-400 text-white shadow-emerald-500/50 shadow-sm',
        glowClass: 'ring-1 ring-emerald-400/50',
      },
      3: {
        numeral: 'III',
        name: 'Prestige III',
        className: 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white shadow-blue-500/50 shadow-sm',
        glowClass: 'ring-1 ring-blue-400/50',
      },
      4: {
        numeral: 'IV',
        name: 'Prestige IV',
        className: 'bg-gradient-to-r from-yellow-400 to-amber-500 text-amber-900 shadow-yellow-500/50 shadow-md',
        glowClass: 'ring-1 ring-yellow-300/70',
        animation: 'animate-pulse',
      },
      5: {
        numeral: 'V',
        name: 'Prestige V',
        className: 'bg-gradient-to-r from-cyan-300 via-sky-400 to-blue-300 text-white shadow-cyan-400/60 shadow-lg',
        glowClass: 'ring-2 ring-cyan-300/80',
        animation: 'animate-shimmer',
        special: true,
      },
    };
    return emblems[prestige] || null;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen game-gradient-bg pattern-dots">
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            size="sm"
            className="font-body bg-card"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Game
          </Button>
          
          <Badge variant="secondary" className="font-display">
            Top 100 Players
          </Badge>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3">
            <div className="relative">
              <Trophy className="w-16 h-16 text-warning" />
              <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-warning animate-sparkle" />
            </div>
          </div>
          <h1 className="font-display text-4xl font-bold text-foreground">
            Leaderboard
          </h1>
          <p className="font-body text-muted-foreground mt-2">
            The greatest fruit crushers of all time!
          </p>
        </div>

        {/* Leaderboard */}
        <Card className="shadow-playful border-2 border-border">
          <CardHeader className="pb-3">
            <CardTitle className="font-display text-lg flex items-center justify-between">
              <span>Rankings</span>
              <span className="text-sm font-body text-muted-foreground">
                {leaderboard.length} players
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
                <p className="font-body text-muted-foreground">
                  Loading leaderboard...
                </p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">😕</div>
                <p className="font-display text-xl text-foreground mb-2">
                  Oops!
                </p>
                <p className="font-body text-muted-foreground mb-4">
                  {error}
                </p>
                <Button
                  onClick={() => window.location.reload()}
                  className="font-display"
                >
                  Try Again
                </Button>
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🏆</div>
                <p className="font-display text-xl text-foreground mb-2">
                  No scores yet!
                </p>
                <p className="font-body text-muted-foreground">
                  Be the first to set a high score!
                </p>
                <Button
                  onClick={() => navigate("/")}
                  className="mt-4 font-display"
                >
                  Play Now
                </Button>
              </div>
            ) : (
              <ScrollArea className="h-[500px] pr-4">
                <div className="space-y-2">
                  {leaderboard.map((entry, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-2 p-2.5 rounded-xl border-2 transition-all hover:scale-[1.01] ${getRankBg(index + 1)}`}
                    >
                      {/* Rank */}
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                        {getRankIcon(index + 1)}
                      </div>

                      {/* Player Avatar */}
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 border-2 border-primary/30 flex items-center justify-center flex-shrink-0">
                        <span className="font-display font-bold text-primary text-base">
                          {entry.playerName?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>

                      {/* Player Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-display font-bold text-foreground truncate text-sm">
                            {entry.playerName || 'Unknown Player'}
                          </p>
                          {/* Level Badge - Platinum for Master, Blue for normal */}
                          <Badge 
                            variant="outline" 
                            className={`text-[10px] px-1.5 py-0 h-4 ${
                              entry.isMaster 
                                ? 'bg-gradient-to-r from-slate-200 to-zinc-100 border-slate-300 text-slate-700 font-semibold' 
                                : 'bg-blue-500/10 border-blue-400/30 text-blue-600'
                            }`}
                          >
                            Lv.{entry.level || 1}
                          </Badge>
                          {/* Prestige Emblem */}
                          {entry.prestige > 0 && (() => {
                            const emblem = getPrestigeEmblem(entry.prestige, entry.isMaster);
                            if (!emblem) return null;
                            return (
                              <Badge 
                                className={`text-[10px] px-1.5 py-0 h-4 font-bold border-0 ${emblem.className} ${emblem.glowClass} ${emblem.animation || ''}`}
                                title={emblem.name}
                              >
                                {emblem.special && <Sparkles className="w-2.5 h-2.5 mr-0.5 inline" />}
                                {emblem.numeral}
                              </Badge>
                            );
                          })()}
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                          <Calendar className="w-2.5 h-2.5" />
                          <span className="font-body">{formatDate(entry.date)}</span>
                        </div>
                      </div>

                      {/* Score - prominent badge */}
                      <div className="flex-shrink-0 bg-gradient-to-r from-amber-400 to-yellow-400 px-2.5 py-1.5 rounded-lg shadow-sm">
                        <p className="font-display text-base font-bold text-amber-900 text-center">
                          {entry.score?.toLocaleString() || 0}
                        </p>
                        <p className="font-body text-[8px] text-amber-800/80 uppercase tracking-wider text-center">
                          points
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
